import PouchDB from 'pouchdb-browser';
import { getDecoded } from '@/lib/auth';
import { LEGACY_LOCAL_DB_NAME, getRemoteDB } from '@/lib/couch';

// One-time carry-over from the shared pre-split replica (`ft-local`) into the
// server, run once per page load for the signed-in login after the per-login
// split. That replica may hold edits the server never accepted — the client's
// 19 Oct "tenant_id immutable" refusals sit there — and, on a shared browser,
// another tenant's data. So only docs tagged with the signed-in tenant are
// carried, only revisions the server has never seen, and only where they are
// newer than the server copy (the same last-write-wins rule as conflicts.ts).

export interface LegacyDoc {
  _id: string;
  _rev: string;
  type?: string;
  tenant_id?: string;
  updatedAt?: number | string;
  _attachments?: unknown;
  [k: string]: unknown;
}

export type RemoteState =
  | { kind: 'missing' }
  | { kind: 'deleted' }
  | { kind: 'present'; rev: string; updatedAt?: number | string };

export interface AdoptionPlan {
  writes: Record<string, unknown>[];
  /** Unsent docs of another tenant: left in the legacy replica, untouched. */
  foreign: number;
  /** Server copy is newer or was deleted there. */
  superseded: number;
}

export function stampMs(v: number | string | undefined): number {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const ms = Date.parse(v);
    return Number.isNaN(ms) ? 0 : ms;
  }
  return 0;
}

/** Decide what to send, given the legacy docs whose current revision the
 *  server lacks and the server's state for each of them. `previous` are the
 *  tenants this account was moved away from: its own edits tagged with one of
 *  them come along, re-tagged to the current tenant. Pure. */
export function planAdoption(
  unsent: LegacyDoc[],
  remote: Map<string, RemoteState>,
  tenantId: string,
  previous: readonly string[] = []
): AdoptionPlan {
  const plan: AdoptionPlan = { writes: [], foreign: 0, superseded: 0 };
  const own = new Set([tenantId, ...previous]);
  for (const doc of unsent) {
    if (doc._id.startsWith('_design/')) continue;
    if (!doc.tenant_id || !own.has(doc.tenant_id)) {
      plan.foreign++;
      continue;
    }
    const state = remote.get(doc._id) ?? { kind: 'missing' };
    if (state.kind === 'deleted') {
      plan.superseded++;
      continue;
    }
    if (
      state.kind === 'present' &&
      stampMs(state.updatedAt) >= stampMs(doc.updatedAt)
    ) {
      plan.superseded++;
      continue;
    }
    // Attachment stubs cannot be re-sent without their bodies; photos live in
    // object storage behind photo_ref docs anyway.
    const { _rev, _attachments, ...rest } = doc;
    const body = { ...rest, tenant_id: tenantId };
    plan.writes.push(
      state.kind === 'present' ? { ...body, _rev: state.rev } : body
    );
  }
  return plan;
}

async function legacyExists(): Promise<boolean> {
  const idb = globalThis.indexedDB as
    | (IDBFactory & { databases?: () => Promise<{ name?: string }[]> })
    | undefined;
  if (!idb) return false;
  if (typeof idb.databases === 'function') {
    const dbs = await idb.databases();
    return dbs.some(d => d.name === `_pouch_${LEGACY_LOCAL_DB_NAME}`);
  }
  // No listing API: opening creates an empty DB, which says "nothing there".
  const db = new PouchDB(LEGACY_LOCAL_DB_NAME);
  const info = await db.info();
  if (info.doc_count === 0) {
    await db.destroy();
    return false;
  }
  await db.close();
  return true;
}

const CHUNK = 200;

/** Carry unsent edits of the signed-in tenant from the legacy replica to the
 *  server, then delete the replica. Leaves it in place when it still holds
 *  another tenant's unsent docs or when anything failed, so nothing is lost;
 *  a later start retries. */
export async function adoptLegacyLocal(): Promise<AdoptionPlan | null> {
  const tenantId = getDecoded()?.tenant_id;
  const previous = getDecoded()?.prev_tenants ?? [];
  const isAdmin = (getDecoded()?.roles ?? []).includes('org_admin');
  const remote = getRemoteDB();
  if (!tenantId || !remote) return null;
  if (!(await legacyExists())) return null;

  const legacy = new PouchDB(LEGACY_LOCAL_DB_NAME);
  let keep = false;
  try {
    const all = await legacy.allDocs<LegacyDoc>({ include_docs: true });
    const docs = all.rows
      .map(r => r.doc as LegacyDoc | undefined)
      .filter((d): d is LegacyDoc => !!d && !d._id.startsWith('_design/'));

    const unsent: LegacyDoc[] = [];
    const state = new Map<string, RemoteState>();
    for (let i = 0; i < docs.length; i += CHUNK) {
      const chunk = docs.slice(i, i + CHUNK);
      const diff = await remote.revsDiff(
        Object.fromEntries(chunk.map(d => [d._id, [d._rev]]))
      );
      const missing = chunk.filter(d => diff[d._id]);
      if (!missing.length) continue;
      unsent.push(...missing);
      const cur = await remote.allDocs<LegacyDoc>({
        keys: missing.map(d => d._id),
        include_docs: true,
      });
      for (const row of cur.rows) {
        if ('error' in row) {
          state.set(row.key, { kind: 'missing' });
        } else if (row.value.deleted) {
          state.set(row.id, { kind: 'deleted' });
        } else {
          state.set(row.id, {
            kind: 'present',
            rev: row.value.rev,
            updatedAt: (row.doc as LegacyDoc | undefined)?.updatedAt,
          });
        }
      }
    }

    const plan = planAdoption(unsent, state, tenantId, previous);
    for (let i = 0; i < plan.writes.length; i += CHUNK) {
      const res = await remote.bulkDocs(plan.writes.slice(i, i + CHUNK));
      for (const r of res) {
        if ('error' in r && r.error) {
          // Anything but a refusal (conflict, network) is worth another try on
          // the next start. A refusal is final only for an admin: on a shared
          // device the edit may be another login's, one allowed to write it.
          const forbidden = r.name === 'forbidden' || r.error === 'forbidden';
          if (!forbidden || !isAdmin) keep = true;
          console.warn('[legacy-local] not carried over:', r);
        }
      }
    }
    if (plan.foreign > 0) keep = true;
    console.info('[legacy-local] carried over', {
      sent: plan.writes.length,
      superseded: plan.superseded,
      foreign: plan.foreign,
    });
    return plan;
  } catch (err) {
    keep = true;
    console.warn('[legacy-local] carry-over postponed:', err);
    return null;
  } finally {
    try {
      if (keep) await legacy.close();
      else await legacy.destroy();
    } catch {
      /* ignore */
    }
  }
}
